import { Flex, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import React, { useMemo, useCallback } from 'react';
import { P, match } from 'ts-pattern';

import { logError } from '~/common/utils/log';
import { CurrencyConvertor } from '~/components/utils/formatters';
import { reportEvent } from '~/domains/analytics';
import useRedirectToEO from '~/domains/common/utils/useRedirectToEO';
import { useEntrantsReferralLink } from '~/domains/common/hooks/useEntrantsReferralLink';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { Button } from '~/domains/design-system/Button';
import { IconChevronRight } from '~/domains/design-system/icons/IconChevronRight';
import { IconPlus } from '~/domains/design-system/icons/IconPlus';
import { ContestAlert } from '~/domains/manage-contest/components/ContestAlert/ContestAlert';
import { useServiceFeeStatus } from '~/domains/manage-contest/hooks/useServiceFeeStatus';
import { mapAlertStatus } from '~/domains/manage-contest/utils/serviceFeeAlertUtils';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import dt from '~/testing';

import EntryButton from '../../../detail/components/EntryButton/EntryButton';
import { getIsEOContest } from '../../utils/getIsEOContest';

import ContestCardActionsBanner from './ContestCardActionsBanner';
import type { ContestCardActionBanner, ContestCardProps } from './types';

function Action({
  href,
  children,
  icon,
  onClick,
}: {
  href?: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
}) {
  const theme = useMantineTheme();

  return (
    <Text
      href={href}
      component={href ? Link : 'button'}
      onClick={onClick}
      variant="subhead-small"
      sx={{
        background: 'transparent',
        padding: 0,
        border: 0,
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: theme.colors.blue[8],
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'underline',
        },
        svg: {
          pointerEvents: 'none',
        },
      }}
    >
      {children}
      {icon && icon}
    </Text>
  );
}

function ActionButton({
  children,
  onClick,
  flex = 1,
  isLoading,
  isDisabled,
}: {
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
  flex?: number;
  isLoading?: boolean;
  isDisabled?: boolean;
}) {
  return (
    <Button
      data-test-id={dt.common.components.contestCardButtons}
      size="medium"
      onClick={onClick}
      variant="tertiary-fill"
      style={{ flex }}
      loading={isLoading}
      disabled={isDisabled}
    >
      {children}
    </Button>
  );
}

export function ContestCardActions({
  contest: { following, followed, ...contest },
  isManageContestEnabled,
  isMassCommunicationEnabled,
  isPlayWithPeopleYouFollowEnabled,
  isEntertainmentOnly,
  isShareContestEnabled,
  actionBanner,
  isCommissioner,
}: {
  contest: ContestCardProps['contest'];
  isManageContestEnabled: boolean;
  isMassCommunicationEnabled: boolean;
  isPlayWithPeopleYouFollowEnabled: boolean;
  isEntertainmentOnly: boolean;
  isShareContestEnabled: boolean;
  actionBanner?: ContestCardActionBanner;
  isCommissioner: boolean;
}) {
  const { t } = useTranslation('contest');
  const isSpectatorMode = contest?.public_viewing;
  const theme = useMantineTheme();
  const { createReferralLink } = useEntrantsReferralLink(contest.id, contest.contest_type);
  const { redirectToEO, isFetching, isRedirecting, requestedURL } = useRedirectToEO();
  // v2-parsed contests carry entitySource, not source; prefer it (matches getContestNavigationTarget).
  const isEOContest = getIsEOContest(
    (contest as { entitySource?: string }).entitySource ?? contest.source
  );
  const { isServiceFeeOverdue, serviceFeeAlertForDisplay } = useServiceFeeStatus(
    contest as FullContest
  );

  const handleManageClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!contest.manage_contest_url) return;

      redirectToEO(contest.manage_contest_url);
    },
    [contest.manage_contest_url, redirectToEO]
  );

  const handleShareClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      createReferralLink(isEOContest && contest.share_url);
    },
    [contest.share_url, createReferralLink, isEOContest]
  );

  const handleRestartClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!contest.restart_url) return;

      let parsedURL: URL | null = null;
      try {
        parsedURL = new URL(contest.restart_url);
      } catch {
        parsedURL = null;
      }

      if (!parsedURL || (parsedURL.protocol !== 'http:' && parsedURL.protocol !== 'https:')) {
        logError({
          errorMessage: 'Invalid or unsupported restart URL',
          context: 'ContestCardActions.handleRestartClick',
          severity: 'error',
          details: { restartUrl: contest.restart_url },
        });
        return;
      }

      window.location.assign(parsedURL.toString());
    },
    [contest.restart_url]
  );

  const commissionerActions = useMemo(() => {
    const actions = [];

    // Only render the manage button when the BE returns a manage url.
    if (isManageContestEnabled && !contest.restart_url && contest.manage_contest_url) {
      actions.push(
        <ActionButton
          key={isServiceFeeOverdue ? 'service-fee' : 'manage'}
          onClick={handleManageClick}
          isLoading={isFetching && requestedURL === contest.manage_contest_url}
          isDisabled={isFetching || isRedirecting}
        >
          <Text variant="body-medium" fw={900} color={theme.colors.gray[9]}>
            {isServiceFeeOverdue
              ? t('manage-contest:serviceFeePage.payServiceFee')
              : t('common.cardActions.manageContest')}
          </Text>
        </ActionButton>
      );
    }

    // Gate on restart_url presence, not source (source parity).
    if (contest.restart_url) {
      actions.push(
        <ActionButton
          key="restart"
          onClick={handleRestartClick}
          isLoading={isRedirecting}
          isDisabled={isRedirecting}
        >
          <Text variant="body-medium" fw={900} color={theme.colors.gray[9]}>
            {t('common.cardActions.restartContest')}
          </Text>
        </ActionButton>
      );
    }

    // Do not show share contest button for EO contests that were not restarted yet
    if (isShareContestEnabled) {
      actions.push(
        <ActionButton key="share" onClick={handleShareClick}>
          <Text variant="body-medium" fw={900} color={theme.colors.gray[9]}>
            {t('common.cardActions.shareContest_commish')}
          </Text>
        </ActionButton>
      );
    }

    return actions;
  }, [
    contest.restart_url,
    contest.manage_contest_url,
    isFetching,
    isRedirecting,
    requestedURL,
    isManageContestEnabled,
    isShareContestEnabled,
    isServiceFeeOverdue,
    handleManageClick,
    handleShareClick,
    handleRestartClick,
    theme.colors.gray,
    t,
  ]);

  const nonCommissionerActions = useMemo(() => {
    const actions = [];

    if (isMassCommunicationEnabled) {
      actions.push(
        <Action key="mass-comm" href="" icon={<IconChevronRight color={theme.colors.blue[8]} />}>
          {t('common.cardActions.massCommunication')}
        </Action>
      );
    }

    if (isShareContestEnabled) {
      actions.push(
        <Action key="share" onClick={handleShareClick}>
          {t('common.cardActions.shareContest')}
        </Action>
      );
    }

    return actions;
  }, [isMassCommunicationEnabled, isShareContestEnabled, handleShareClick, theme.colors.blue, t]);

  return (
    <Flex
      direction="column"
      /* eslint-disable-line local-rules/use-theme-tokens */ gap="16px"
      /* eslint-disable-line local-rules/use-theme-tokens */ mt="18px"
      sx={{
        '.mantine-Grid-root + &': {},
      }}
    >
      {/* Commissioner Actions (Row Layout) */}
      {isCommissioner && commissionerActions.length > 0 && (
        <Flex direction="row" /* eslint-disable-line local-rules/use-theme-tokens */ gap="8px" wrap="wrap" w="100%">
          {commissionerActions}
        </Flex>
      )}

      {actionBanner && <ContestCardActionsBanner actionBanner={actionBanner} />}

      {/* Service Fee Alert Banner (only for commissioners with service fee alerts) */}
      {isCommissioner && serviceFeeAlertForDisplay && (
        <ContestAlert
          status={mapAlertStatus(serviceFeeAlertForDisplay.status)}
          message={serviceFeeAlertForDisplay.message}
          variant="banner"
        />
      )}

      {/* Non-Commissioner Actions (Column Layout) */}
      {!isCommissioner && nonCommissionerActions.length > 0 && (
        <Flex direction="column" /* eslint-disable-line local-rules/use-theme-tokens */ gap="16px">
          {nonCommissionerActions}
        </Flex>
      )}

      {/* Play With People You Follow (always column layout) */}
      {isPlayWithPeopleYouFollowEnabled && (
        <Flex w="100%" justify="space-between" align="center">
          <Title variant="title-small">
            {match({ following, followed })
              .with({ following: P.when(() => following > 0) }, () =>
                t('home:playWithPeopleYouFollow.text.followeesEntered__hasPlural', {
                  count: following,
                })
              )
              .with({ followed: P.when(() => followed > 0) }, () =>
                t('home:playWithPeopleYouFollow.text.followersEntered__hasPlural', {
                  count: followed,
                })
              )
              .otherwise(() => null)}
          </Title>
          {isEOContest ? (
            <Action
              icon={<IconPlus />}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                redirectToEO(contest.game_center_url || contest.share_url);
              }}
            >
              {t('home:playWithPeopleYouFollow.cta__hasPlural', {
                count: contest.entries.max_per_user ?? 0 - (contest.entries.user ?? 0),
              })}
            </Action>
          ) : (
            <EntryButton
              contestId={contest.id}
              entries={contest.entries}
              isAfterDeadline={contest.is_after_first_late_swap_deadline}
              status={contest.status}
              isAllowedInLocation={contest.is_allowed_in_location}
              isEntertainmentOnly={isEntertainmentOnly}
              isSpectatorMode={isSpectatorMode}
              onSuccess={(entryIDs) => {
                reportEvent('Lobby > Add Entries', {
                  followers_playing: followed,
                  followees_playing: following,
                  entry_fee_usd: CurrencyConvertor(contest.entry_fee / 100),
                  entries_count: entryIDs.length,
                });
              }}
            >
              <Action
                icon={<IconPlus />}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                {t('home:playWithPeopleYouFollow.cta__hasPlural', {
                  count: contest.entries.max_per_user ?? 0 - (contest.entries.user ?? 0),
                })}
              </Action>
            </EntryButton>
          )}
        </Flex>
      )}
    </Flex>
  );
}
